import * as React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  Grid2,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DateRangeOptions } from "../Utility/Constants";
import { getDisplayValue } from "../Utility/Utility";
import { useState, useEffect } from "react";

const dateRanges = [
  DateRangeOptions.Today,
  DateRangeOptions.Yesterday,
  DateRangeOptions.Next7Days,
  DateRangeOptions.Last7Days,
  DateRangeOptions.Last30Days,
  DateRangeOptions.Next30Days,
  DateRangeOptions.Custom,
];

// Key for localStorage
const STORAGE_KEY = "dateRangeSelectedForVisits";

// Helper function to load from localStorage
const loadFromStorage = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved ? JSON.parse(saved) : null;
};

// Helper function to save to localStorage
const saveToStorage = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

export default function DateRangeDialog({
  filters,
  title,
  setFilters,
  openState,
  setOpenState,
}) {
  // Load saved state from localStorage
  const savedState = loadFromStorage();

  // Determine initial selected option
  const initialSelectedOption = 
    savedState?.selectedDateRangeOption || 
    filters.selectedDateRangeOption || 
    DateRangeOptions.Today;

  // Determine initial dates
  const initialFrom = savedState?.from 
    ? dayjs(savedState.from) 
    : dayjs(filters.from || new Date());
  
  const initialTo = savedState?.to 
    ? dayjs(savedState.to) 
    : dayjs(filters.to || new Date());

  const [value, setValue] = useState(initialSelectedOption);
  const [startDate, setStartDate] = useState(initialFrom);
  const [endDate, setEndDate] = useState(initialTo);
  const [selectedOption, setSelectedOption] = useState(initialSelectedOption);
  const [customStartDate, setCustomStartDate] = useState(initialFrom);
  const [customEndDate, setCustomEndDate] = useState(initialTo);

  // Effect to sync with parent filters and localStorage on component mount
  useEffect(() => {
    // If there's a saved state in localStorage, use it to update filters
    if (savedState) {
      const updatedFilters = {
        ...filters,
        selectedDateRangeOption: savedState.selectedDateRangeOption,
        from: dayjs(savedState.from),
        to: dayjs(savedState.to)
      };

      // Update parent component's filters
      setFilters(updatedFilters);

      // Set local state
      setValue(savedState.selectedDateRangeOption);
      setSelectedOption(savedState.selectedDateRangeOption);
      
      // Handle custom vs predefined ranges
      if (savedState.selectedDateRangeOption === DateRangeOptions.Custom) {
        setCustomStartDate(dayjs(savedState.from));
        setCustomEndDate(dayjs(savedState.to));
      } else {
        updateDateRange(savedState.selectedDateRangeOption, false);
      }
    }
  }, []); // Empty dependency array ensures this runs only on mount

  const updateDateRange = (range, updateSelection = true) => {
    let newStartDate, newEndDate;
    
    switch (range) {
      case DateRangeOptions.Today:
        newStartDate = dayjs();
        newEndDate = dayjs();
        break;
      case DateRangeOptions.Yesterday:
        newStartDate = dayjs().subtract(1, "day");
        newEndDate = dayjs().subtract(1, "day");
        break;
      case DateRangeOptions.Next7Days:
        newStartDate = dayjs();
        newEndDate = dayjs().add(7, "day");
        break;
      case DateRangeOptions.Last7Days:
        newStartDate = dayjs().subtract(7, "day");
        newEndDate = dayjs();
        break;
      case DateRangeOptions.Last30Days:
        newStartDate = dayjs().subtract(30, "day");
        newEndDate = dayjs();
        break;
      case DateRangeOptions.Next30Days:
        newStartDate = dayjs();
        newEndDate = dayjs().add(30, "day");
        break;
      case DateRangeOptions.Custom:
        // Don't update dates for custom range
        if (updateSelection) setSelectedOption(DateRangeOptions.Custom);
        return;
      default:
        return;
    }
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    if (updateSelection) setSelectedOption(range);
    
    // Removed the setFilters call here to prevent API call on radio button change
  };

  const handleRadioChange = (e) => {
    const selectedValue = e.target.value;
    setValue(selectedValue);
    updateDateRange(selectedValue);
  };

  const handleApply = () => {
    setOpenState(false);
    const newFilters = {
      ...filters,
      selectedDateRangeOption: selectedOption,
    };
    
    if (selectedOption === DateRangeOptions.Custom) {
      newFilters.from = customStartDate;
      newFilters.to = customEndDate;
    } else {
      newFilters.from = startDate;
      newFilters.to = endDate;
    }
    
    // Enhanced localStorage saving
    saveToStorage({
      selectedDateRangeOption: selectedOption,
      from: selectedOption === DateRangeOptions.Custom ? customStartDate : startDate,
      to: selectedOption === DateRangeOptions.Custom ? customEndDate : endDate,
    });
    
    // Update parent component's filters - THIS TRIGGERS API CALL
    setFilters(newFilters);
  };

  return (
    <Dialog open={openState}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Grid2 container size={12}>
          <RadioGroup 
            row 
            value={value} 
            onChange={handleRadioChange}
          >
            {dateRanges.map((range) => (
              <Grid2 size={6} key={range}>
                <FormControlLabel
                  value={range}
                  control={<Radio/>}
                  label={getDisplayValue(range)}
                />
              </Grid2>
            ))}
          </RadioGroup>
        </Grid2>
        <Grid2 container size={12}>
          <Grid2 container size={6} spacing={1} sx={{marginTop:"5px"}}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From"
                disabled={selectedOption !== DateRangeOptions.Custom}
                value={customStartDate}
                maxDate={dayjs(customEndDate)}
                onChange={(newValue) => setCustomStartDate(newValue)}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 container size={6} spacing={1} sx={{marginTop:"5px"}}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To"
                disabled={selectedOption !== DateRangeOptions.Custom}
                minDate={dayjs(customStartDate)}
                value={customEndDate}
                onChange={(newValue) => setCustomEndDate(newValue)}
              />
            </LocalizationProvider>
          </Grid2>
        </Grid2>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={() => setOpenState(false)} 
          variant="outlined" 
          color="inherit"
        >
          Close
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="secondary"
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}